import React, { useEffect } from 'react';
import UnifiedNavigation from '../Navigation/UnifiedNavigation';
import Footer from '../Footer/Footer';
import InstagramSection from '../BrandHomePage/InstagramSection';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import './GettingHereTemplate.css';
import { images } from '../shared/assets';
import { CTALink } from '../shared/CTALink/CTALink';

export interface GettingHereProps {
  variant: 'patagonia' | 'atacama' | 'iguazu' | 'mendoza' | 'santa-catarina';
  heroTitle: string;
  heroDescription: string;
  heroImage: string;
  locationInfo: {
    title: string;
    address: string;
    directions: Array<{
      title: string;
      description: string;
    }>;
    nearbyBorders?: Array<{
      country: string;
      border: string;
      distance: string;
    }>;
  };
  transferInfo: string;
}

const GettingHereTemplate: React.FC<GettingHereProps> = ({
  variant,
  heroTitle,
  heroDescription,
  heroImage,
  locationInfo,
  transferInfo
}) => {
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, [setHoveredDestination]);

  // Get color based on variant
  const getVariantColor = (variant: string): string => {
    const colors: Record<string, string> = {
      'patagonia': '#9fa9ad',
      'atacama': '#7b6569',
      'iguazu': '#3e5b58',
      'mendoza': '#502630',
      'santa-catarina': '#009485'
    };
    return colors[variant] || '#49413d';
  };

  // Asset URLs
  const mapNavigation = '/assets/images/map-navigation.svg';
  const markerArrow = '/assets/marker-arrow.svg';
  const propertyLogo = variant === 'santa-catarina' ? images.logos.santaCatarina : images.logos[variant as keyof typeof images.logos];

  return (
    <div className={`getting-here-template getting-here-${variant}`}>
      {/* Navigation */}
      <UnifiedNavigation variant="light" destination={variant} />

      {/* Hero Text Block - Figma Exact (3138-37690) */}
      <section className="text-block-header-section" data-scroll-section>
        <div className="text-block-container">
          <h1 className="text-block-title">{heroTitle}</h1>
          <p className="text-block-description">{heroDescription}</p>
        </div>
      </section>

      {/* Full Image Section - Figma Exact (3138-37691) */}
      <section className="full-image-section" data-scroll-section>
        <div className="full-image-container">
          <div 
            className="full-image-background"
            style={{ backgroundImage: `url('${heroImage}')` }}
          />
        </div>
      </section>

      {/* Map Block - Figma Exact (3138-37737) */}
      <section className="map-block-section" data-scroll-section>
        <div className="map-block-container">
          {/* Map Side - Static Figma Design Only */}
          <div className="map-side">
            <div 
              className="map-background"
              style={{ backgroundImage: `url('/assets/figma/e81ab367ef65525aad1480d1faddeb5f7050ad91.png')` }}
            >
              {/* Map Navigation */}
              <div 
                className="map-navigation"
                style={{ backgroundImage: `url('/assets/figma/7480369bf8335012f137f5425989842c4b1fc8cf.png')` }}
              />
              
              {/* Awasi Tag/Marker */}
              <div className="map-tag">
                <div className="map-tag-container" style={{ backgroundColor: getVariantColor(variant) }}>
                  <div className="map-tag-logo">
                    <img src={propertyLogo} alt="Awasi" className="awasi-logo" />
                  </div>
                </div>
                <div className="map-tag-arrow">
                  <img src="/assets/figma/a33ba1312186de6cc92a2a3b035bfed60a30305c.svg" alt="Arrow pointing to the location" />
                </div>
              </div>
            </div>
          </div>

          {/* Text Block Side */}
          <div className="text-block-side" style={{ backgroundColor: getVariantColor(variant) }}>
            <h2 className="map-title">{locationInfo.title}</h2>
            
            <div className="map-text-content">
              <div className="map-text-section">
                <div className="map-info-item">
                  <h3 className="map-label">ADDRESS</h3>
                  <p className="map-text">{locationInfo.address}</p>
                </div>

                {locationInfo.directions.map((direction, index) => (
                  <div key={index} className="map-info-item">
                    <h3 className="map-label">{direction.title}</h3>
                    <p className="map-text">{direction.description}</p>
                  </div>
                ))}

                <div className="map-divider">
                  <img src="/assets/figma/93ee095826c64c5ac62c273a5febd82ad8756b9f.svg" alt="Divider" />
                </div>
              </div>

              {locationInfo.nearbyBorders && locationInfo.nearbyBorders.length > 0 && (
                <div className="map-info-item">
                  <h3 className="map-label">NEARBY LAND BORDERS</h3>
                  <div className="map-borders">
                    {locationInfo.nearbyBorders.map((border, index) => (
                      <p key={index} className="map-text">{border.country}: {border.border} ({border.distance})</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <CTALink 
              text="CONTACT US"
              type="primary"
              color="white"
              href="/contact"
              className="map-contact-btn"
            />
          </div>
        </div>
      </section>

      {/* Transfer Information Section */}
      <section className="getting-transfer-section" data-scroll-section>
        <div className="getting-transfer-content">
          <h2 className="getting-transfer-title">{transferInfo}</h2>
          <CTALink 
            text="CONTACT US"
            type="primary"
            color="awasi"
            href="/contact"
            className="getting-transfer-btn"
          />
        </div>
      </section>

      {/* Instagram Gallery */}
      <div className="instagram-section-wrapper">
        <InstagramSection />
      </div>

      {/* Footer */}
      <Footer variant={variant} />
    </div>
  );
};

export default GettingHereTemplate;